import type { VisioReviewConfig } from '@gozoki/api-types'
import {
  type QueryClient,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  saveManagerEvaluation,
  updateVisioReviewConfig,
  validateVisioReviewAiGrade,
} from '../mutations/visioReviews.mutations'
import {
  fetchVisioReviewConfig,
  fetchVisioReviewFilterOptions,
  type FetchVisioReviewFilterOptionsParams,
  fetchVisioReviewInteractions,
  fetchVisioReviewInteractionsToEvaluate,
  type FetchVisioReviewInteractionsToEvaluateParams,
  fetchVisioReviewOperatorCharts,
  fetchVisioReviewOperatorComments,
  fetchVisioReviewOperatorDetail,
  fetchVisioReviewOperatorInteractions,
  fetchVisioReviewOperators,
  fetchVisioReviewRecordingUrl,
  fetchVisioReviewStoreList,
  type FetchVisioReviewInteractionsParams,
  type FetchVisioReviewOperatorsParams,
  type FetchVisioReviewStoreListParams,
} from '../queries/visioReviews.queries'

const STALE_TIME = 120_000

const hasMorePageParam = <T extends { hasMorePage: boolean }>(lastPage: T, allPages: T[]) =>
  lastPage.hasMorePage ? allPages.length + 1 : undefined

export const useVisioReviewInteractions = (params: FetchVisioReviewInteractionsParams) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'interactions', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewInteractions({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperators = (params: FetchVisioReviewOperatorsParams) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'operators', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewOperators({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorCharts = (params: {
  dateStart?: string
  dateEnd: string
  interactionTypes?: string
}) =>
  useQuery({
    queryKey: ['visio-reviews', 'operators', 'charts', params],
    queryFn: () => fetchVisioReviewOperatorCharts(params),
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorDetail = (
  userId: number | null,
  params: { dateStart?: string; dateEnd: string; interactionTypes?: string },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['visio-reviews', 'operator-detail', userId, params],
    queryFn: userId ? () => fetchVisioReviewOperatorDetail(userId, params) : skipToken,
    enabled: (options?.enabled ?? true) && userId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorInteractions = (
  userId: number | null,
  params: { dateStart?: string; dateEnd: string; interactionTypes?: string },
  options?: { enabled?: boolean; pageSize?: number },
) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'operator-interactions', userId, params, options?.pageSize],
    queryFn: userId
      ? ({ pageParam = 1 }: { pageParam?: number }) =>
          fetchVisioReviewOperatorInteractions(userId, {
            ...params,
            page: pageParam,
            pageSize: options?.pageSize ?? 5,
          })
      : skipToken,
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    enabled: (options?.enabled ?? true) && userId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorComments = (
  userId: number | null,
  params: { dateStart?: string; dateEnd: string; interactionTypes?: string },
  options?: { enabled?: boolean; pageSize?: number },
) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'operator-comments', userId, params, options?.pageSize],
    queryFn: userId
      ? ({ pageParam = 1 }: { pageParam?: number }) =>
          fetchVisioReviewOperatorComments(userId, {
            ...params,
            page: pageParam,
            pageSize: options?.pageSize ?? 5,
          })
      : skipToken,
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    enabled: (options?.enabled ?? true) && userId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewStoreList = (params: Omit<FetchVisioReviewStoreListParams, 'page'>) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'stores', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewStoreList({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

// Returns a short-lived presigned S3 URL for the recorded visio. The URL
// expires (~15 min) so we treat the cache entry as always-stale: reopening
// the modal triggers a fresh fetch instead of handing back a dead signature.
export const useVisioReviewRecordingUrl = (reviewId: number | null) =>
  useQuery({
    queryKey: ['visio-reviews', 'recording-url', reviewId],
    queryFn: reviewId ? () => fetchVisioReviewRecordingUrl(reviewId) : skipToken,
    enabled: reviewId !== null,
    staleTime: 0,
    gcTime: 10 * 60 * 1000, // 10 min — below the 15-min presigned URL TTL
    retry: false,
  })

const invalidateVisioReviewData = (queryClient: QueryClient) => {
  // Immediately refetch the active evaluation views
  queryClient.invalidateQueries({ queryKey: ['visio-reviews', 'interactions'] })
  queryClient.invalidateQueries({ queryKey: ['visio-reviews', 'to-evaluate'] })
  // Mark others as stale without triggering an immediate refetch
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'operators'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'operator-detail'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'operator-interactions'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'operator-comments'],
    refetchType: 'none',
  })
}

export const useSaveManagerEvaluation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      reviewId,
      data,
    }: {
      reviewId: number
      data: { managerGrade: number; managerComment?: string }
    }) => saveManagerEvaluation(reviewId, data),
    onSuccess: () => {
      invalidateVisioReviewData(queryClient)
    },
  })
}

export const useVisioReviewConfig = () =>
  useQuery({
    queryKey: ['visio-reviews', 'config'],
    queryFn: fetchVisioReviewConfig,
    staleTime: STALE_TIME,
  })

export const useUpdateVisioReviewConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<VisioReviewConfig>) => updateVisioReviewConfig(data),
    onSuccess: (data) => {
      queryClient.setQueryData(['visio-reviews', 'config'], data)
    },
  })
}

export const useVisioReviewInteractionsToEvaluate = (
  params: FetchVisioReviewInteractionsToEvaluateParams,
) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'to-evaluate', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewInteractionsToEvaluate({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

export const useValidateVisioReviewAiGrade = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reviewId: number) => validateVisioReviewAiGrade(reviewId),
    onSuccess: () => {
      invalidateVisioReviewData(queryClient)
    },
  })
}

export const useVisioReviewFilterOptions = (params: FetchVisioReviewFilterOptionsParams) =>
  useQuery({
    queryKey: ['visio-reviews', 'filter-options', params],
    queryFn: () => fetchVisioReviewFilterOptions(params),
    staleTime: STALE_TIME,
  })
